import { EllipsisIcon } from 'lucide-react'
import { useValidDevices } from '@/hooks'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/DropdownMenu/DropdownMenu'
import { Button } from '@/components/ui/Button/Button'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/Drawer/Drawer'
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup/RadioGroup'
import { Label } from '@/components/ui/Label/Label'
import { cn } from '@/utils'

type Device = {
  id: string
  label: string
}

type DeviceSelectorProps = {
  audioInputs: Device[]
  audioOutputs: Device[]
  videoInputs?: Device[]
  selectedAudioInput?: string
  selectedAudioOutput?: string
  selectedVideoInput?: string
  onAudioInputChange: (deviceId: string) => void
  onAudioOutputChange: (deviceId: string) => void
  onVideoInputChange?: (deviceId: string) => void
  buttonSize?: 'md' | 'lg'
  label?: string
}

export const DeviceSelector = ({
  audioInputs,
  audioOutputs,
  videoInputs,
  selectedAudioInput,
  selectedAudioOutput,
  selectedVideoInput,
  onAudioInputChange,
  onAudioOutputChange,
  onVideoInputChange,
  buttonSize = 'md',
  label
}: DeviceSelectorProps) => {
  const { validDevices: validAudioInputs, selectValue: audioInputValue } = useValidDevices(
    audioInputs,
    selectedAudioInput
  )
  const { validDevices: validAudioOutputs, selectValue: audioOutputValue } = useValidDevices(
    audioOutputs,
    selectedAudioOutput
  )
  const { validDevices: validVideoInputs, selectValue: videoInputValue } = useValidDevices(
    videoInputs,
    selectedVideoInput
  )

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="hidden lg:flex">
          <div className="flex flex-col gap-y-2 items-center">
            <Button
              size="icon"
              className={cn(
                buttonSize === 'md' ? 'h-[52px] w-[52px]' : 'h-16 w-16',
                'rounded-full bg-[#ffffff]/15 hover:bg-[#ffffff]/50 backdrop-blur-sm  text-white'
              )}
            >
              <EllipsisIcon />
            </Button>
            <span className="text-xs">{label}</span>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-64 p-4 bg-[#73695F]/75 backdrop-blur-md text-white">
          {/* Camera */}
          {validVideoInputs && validVideoInputs.length > 0 && (
            <>
              <DropdownMenuLabel className="ml-6 text-xs font-normal text-gray-200/50">Caméra</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={videoInputValue} onValueChange={onVideoInputChange}>
                {validVideoInputs.map((device) => (
                  <DropdownMenuRadioItem key={device.id} value={device.id} className="text-xs">
                    {device.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </>
          )}

          {/* Microphone */}
          {validAudioInputs && validAudioInputs.length > 0 && (
            <>
              {validVideoInputs && validVideoInputs.length > 0 && <DropdownMenuSeparator className="bg-gray-500/50" />}
              <DropdownMenuLabel className="ml-5 text-xs font-normal text-gray-200/50">Microphone</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={audioInputValue} onValueChange={onAudioInputChange}>
                {validAudioInputs.map((device) => (
                  <DropdownMenuRadioItem key={device.id} value={device.id} className="text-xs">
                    {device.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </>
          )}

          {/* Audio Output */}
          {validAudioOutputs && validAudioOutputs.length > 0 && (
            <>
              <DropdownMenuSeparator className="bg-gray-500/50" />
              <DropdownMenuLabel className="ml-5 text-xs font-normal text-gray-200/50">Haut-parleurs</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={audioOutputValue} onValueChange={onAudioOutputChange}>
                {validAudioOutputs.map((device) => (
                  <DropdownMenuRadioItem key={device.id} value={device.id} className="text-xs">
                    {device.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Drawer>
        <DrawerTrigger asChild className="lg:hidden">
          <div className="flex flex-col gap-y-2 items-center">
            <Button
              size="icon"
              className={cn(
                buttonSize === 'md' ? 'h-[52px] w-[52px]' : 'h-16 w-16',
                'rounded-full bg-[#ffffff]/15 hover:bg-[#ffffff]/50 backdrop-blur-sm  text-white'
              )}
            >
              <EllipsisIcon />
            </Button>
            <span className="text-xs">{label}</span>
          </div>
        </DrawerTrigger>

        <DrawerContent className="bg-[#73695F]/75 backdrop-blur-md text-white p-4">
          {/* Camera */}
          {validVideoInputs && validVideoInputs.length > 0 && (
            <>
              <p className="ml-6 text-xs font-normal text-gray-200/50">Caméra</p>
              <RadioGroup value={videoInputValue} onValueChange={onVideoInputChange} className="gap-1">
                {validVideoInputs.map((device) => (
                  <div key={device.id} className="flex items-center space-x-2 py-1.5 pl-2">
                    <RadioGroupItem value={device.id} id={`camera-${device.id}`} />
                    <Label htmlFor={`camera-${device.id}`} className="text-xs font-normal cursor-pointer">
                      {device.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </>
          )}

          {/* Microphone */}
          {validAudioInputs && validAudioInputs.length > 0 && (
            <>
              {validVideoInputs && validVideoInputs.length > 0 && <div className="my-1 h-px bg-gray-500/50" />}
              <p className="ml-6 text-xs font-normal text-gray-200/50">Microphone</p>
              <RadioGroup value={audioInputValue} onValueChange={onAudioInputChange} className="gap-1">
                {validAudioInputs.map((device) => (
                  <div key={device.id} className="flex items-center space-x-2 py-1.5 pl-2">
                    <RadioGroupItem value={device.id} id={`mic-${device.id}`} />
                    <Label htmlFor={`mic-${device.id}`} className="text-xs font-normal cursor-pointer">
                      {device.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </>
          )}

          {/* Audio Output */}
          {validAudioOutputs && validAudioOutputs.length > 0 && (
            <>
              <div className="my-1 h-px bg-gray-500/50" />
              <p className="ml-6 mb-1 text-xs font-normal text-gray-200/50">Haut-parleurs</p>
              <RadioGroup value={audioOutputValue} onValueChange={onAudioOutputChange} className="gap-1">
                {validAudioOutputs.map((device) => (
                  <div key={device.id} className="flex items-center space-x-2 py-1.5 pl-2">
                    <RadioGroupItem value={device.id} id={`speaker-${device.id}`} />
                    <Label htmlFor={`speaker-${device.id}`} className="text-xs font-normal cursor-pointer">
                      {device.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  )
}
